import {
  BinaryReader,
  BinaryWriter,
  type Command,
  type IssuedCommand,
  type TickBundle,
} from '@am/sim';
import { readCommand, writeCommand } from './command-codec';

/** Bumped when the wire format changes. Client and relay must match. */
export const PROTOCOL_VERSION = 16;

export const MAX_PLAYERS = 4;
export const MAX_NAME_LENGTH = 24;
/** Longest chat line the relay passes on. */
export const MAX_CHAT_LENGTH = 200;

/** 0 = paused, otherwise the speed multiplier. */
export const SPEEDS = [0, 1, 2, 3] as const;

export interface PlayerInfo {
  readonly id: number;
  readonly name: string;
  /** False while the player is still loading the world. */
  readonly ready: boolean;
}

export type ClientMessage =
  | {
      readonly type: 'hello';
      readonly protocolVersion: number;
      readonly simVersion: string;
      readonly name: string;
      /** Empty string creates a new room. */
      readonly roomCode: string;
      /** A saved game to open the new room from; empty for a new world (or when joining). */
      readonly save: Uint8Array;
    }
  | { readonly type: 'command'; readonly command: Command }
  | { readonly type: 'hash'; readonly seq: number; readonly hash: number }
  | { readonly type: 'snapshot'; readonly seq: number; readonly data: Uint8Array }
  | { readonly type: 'ready' }
  | { readonly type: 'setSpeed'; readonly speed: number }
  | { readonly type: 'ping'; readonly time: number }
  /** A chat line for everyone in the room (not part of the simulation). */
  | { readonly type: 'chat'; readonly text: string }
  /** A ping on the map (tile) for everyone in the room. */
  | { readonly type: 'mark'; readonly x: number; readonly z: number };

export type WorldStart =
  | { readonly kind: 'new'; readonly seed: number }
  | { readonly kind: 'snapshot' };

export type ErrorCode =
  | 'versionMismatch'
  | 'roomNotFound'
  | 'roomFull'
  | 'badMessage'
  | 'syncTimeout';

export type ServerMessage =
  | {
      readonly type: 'welcome';
      readonly playerId: number;
      readonly roomCode: string;
      readonly start: WorldStart;
    }
  | { readonly type: 'bundle'; readonly bundle: TickBundle }
  | { readonly type: 'requestSnapshot'; readonly seq: number }
  | { readonly type: 'loadSnapshot'; readonly data: Uint8Array }
  | {
      readonly type: 'roomState';
      readonly players: readonly PlayerInfo[];
      readonly authorityId: number;
      readonly speed: number;
      /** True while the room is paused for a player joining or resyncing. */
      readonly syncing: boolean;
    }
  | { readonly type: 'desync'; readonly seq: number; readonly playerId: number }
  | { readonly type: 'error'; readonly code: ErrorCode; readonly message: string }
  | { readonly type: 'pong'; readonly time: number }
  | { readonly type: 'chat'; readonly playerId: number; readonly text: string }
  | { readonly type: 'mark'; readonly playerId: number; readonly x: number; readonly z: number };

// Wire ids. Append only.
const C = {
  hello: 1,
  command: 2,
  hash: 3,
  snapshot: 4,
  ready: 5,
  setSpeed: 6,
  ping: 7,
  chat: 8,
  mark: 9,
} as const;
const S = {
  welcome: 1,
  bundle: 2,
  requestSnapshot: 3,
  loadSnapshot: 4,
  roomState: 5,
  desync: 6,
  error: 7,
  pong: 8,
  chat: 9,
  mark: 10,
} as const;

export function encodeClientMessage(msg: ClientMessage): Uint8Array<ArrayBuffer> {
  const w = new BinaryWriter(64);
  w.u8(C[msg.type]);
  switch (msg.type) {
    case 'hello':
      w.u16(msg.protocolVersion).string(msg.simVersion).string(msg.name).string(msg.roomCode);
      w.bytes(msg.save);
      break;
    case 'command':
      writeCommand(w, msg.command);
      break;
    case 'hash':
      w.u32(msg.seq).u32(msg.hash);
      break;
    case 'snapshot':
      w.u32(msg.seq).bytes(msg.data);
      break;
    case 'ready':
      break;
    case 'setSpeed':
      w.u8(msg.speed);
      break;
    case 'ping':
      w.f64(msg.time);
      break;
    case 'chat':
      w.string(msg.text);
      break;
    case 'mark':
      w.u16(msg.x).u16(msg.z);
      break;
  }
  return w.finish();
}

export function decodeClientMessage(bytes: Uint8Array): ClientMessage {
  const r = new BinaryReader(bytes);
  const id = r.u8();
  switch (id) {
    case C.hello:
      return {
        type: 'hello',
        protocolVersion: r.u16(),
        simVersion: r.string(),
        name: r.string(),
        roomCode: r.string(),
        save: r.bytes(),
      };
    case C.command:
      return { type: 'command', command: readCommand(r) };
    case C.hash:
      return { type: 'hash', seq: r.u32(), hash: r.u32() };
    case C.snapshot:
      return { type: 'snapshot', seq: r.u32(), data: r.bytes() };
    case C.ready:
      return { type: 'ready' };
    case C.setSpeed:
      return { type: 'setSpeed', speed: r.u8() };
    case C.ping:
      return { type: 'ping', time: r.f64() };
    case C.chat:
      return { type: 'chat', text: r.string() };
    case C.mark:
      return { type: 'mark', x: r.u16(), z: r.u16() };
    default:
      throw new Error(`Unknown client message id ${id}`);
  }
}

const ERROR_CODES: readonly ErrorCode[] = [
  'versionMismatch',
  'roomNotFound',
  'roomFull',
  'badMessage',
  'syncTimeout',
];

export function encodeServerMessage(msg: ServerMessage): Uint8Array<ArrayBuffer> {
  const w = new BinaryWriter(64);
  w.u8(S[msg.type]);
  switch (msg.type) {
    case 'welcome':
      w.u16(msg.playerId).string(msg.roomCode);
      if (msg.start.kind === 'new') w.u8(1).u32(msg.start.seed);
      else w.u8(2);
      break;
    case 'bundle':
      writeBundle(w, msg.bundle);
      break;
    case 'requestSnapshot':
      w.u32(msg.seq);
      break;
    case 'loadSnapshot':
      w.bytes(msg.data);
      break;
    case 'roomState':
      w.u8(msg.players.length);
      for (const p of msg.players) w.u16(p.id).string(p.name).bool(p.ready);
      w.u16(msg.authorityId).u8(msg.speed).bool(msg.syncing);
      break;
    case 'desync':
      w.u32(msg.seq).u16(msg.playerId);
      break;
    case 'error':
      w.u8(ERROR_CODES.indexOf(msg.code)).string(msg.message);
      break;
    case 'pong':
      w.f64(msg.time);
      break;
    case 'chat':
      w.u16(msg.playerId).string(msg.text);
      break;
    case 'mark':
      w.u16(msg.playerId).u16(msg.x).u16(msg.z);
      break;
  }
  return w.finish();
}

export function decodeServerMessage(bytes: Uint8Array): ServerMessage {
  const r = new BinaryReader(bytes);
  const id = r.u8();
  switch (id) {
    case S.welcome: {
      const playerId = r.u16();
      const roomCode = r.string();
      const start: WorldStart =
        r.u8() === 1 ? { kind: 'new', seed: r.u32() } : { kind: 'snapshot' };
      return { type: 'welcome', playerId, roomCode, start };
    }
    case S.bundle:
      return { type: 'bundle', bundle: readBundle(r) };
    case S.requestSnapshot:
      return { type: 'requestSnapshot', seq: r.u32() };
    case S.loadSnapshot:
      return { type: 'loadSnapshot', data: r.bytes() };
    case S.roomState: {
      const count = r.u8();
      const players: PlayerInfo[] = [];
      for (let i = 0; i < count; i++)
        players.push({ id: r.u16(), name: r.string(), ready: r.bool() });
      return { type: 'roomState', players, authorityId: r.u16(), speed: r.u8(), syncing: r.bool() };
    }
    case S.desync:
      return { type: 'desync', seq: r.u32(), playerId: r.u16() };
    case S.error:
      return { type: 'error', code: ERROR_CODES[r.u8()] ?? 'badMessage', message: r.string() };
    case S.pong:
      return { type: 'pong', time: r.f64() };
    case S.chat:
      return { type: 'chat', playerId: r.u16(), text: r.string() };
    case S.mark:
      return { type: 'mark', playerId: r.u16(), x: r.u16(), z: r.u16() };
    default:
      throw new Error(`Unknown server message id ${id}`);
  }
}

function writeBundle(w: BinaryWriter, b: TickBundle): void {
  w.u32(b.seq).bool(b.advance).u16(b.commands.length);
  for (const c of b.commands) {
    w.u16(c.playerId);
    writeCommand(w, c.command);
  }
}

function readBundle(r: BinaryReader): TickBundle {
  const seq = r.u32();
  const advance = r.bool();
  const count = r.u16();
  const commands: IssuedCommand[] = [];
  for (let i = 0; i < count; i++) commands.push({ playerId: r.u16(), command: readCommand(r) });
  return { seq, advance, commands };
}
